/**
 * FilterService - Handles data filtering and ranking calculations
 */

import { AthleteData } from '../types/athlete.types';
import { FilterConfig, RankingMode } from '../types/config.types';

export class FilterService {
    /**
     * Applies filters to athlete data and re-ranks
     */
    applyFilters(
        data: AthleteData[],
        filters: FilterConfig,
        rankingMode: RankingMode
    ): AthleteData[] {
        let filteredData = [...data];

        // Apply category filter
        if (filters.category !== 'all') {
            filteredData = filteredData.filter(d => d.Event === filters.category);
        }

        // Apply distance filter
        if (filters.distance !== 'all') {
            filteredData = filteredData.filter(d => d.Distance === filters.distance);
        }

        // Apply age group filter
        if (filters.ageGroup !== 'all') {
            filteredData = filteredData.filter(d => d['Age Group'] === filters.ageGroup);
        }

        // Sort based on ranking mode
        if (rankingMode === 'individual') {
            filteredData.sort((a, b) => {
                const aTime = a.individualRaceTime || Number.MAX_SAFE_INTEGER;
                const bTime = b.individualRaceTime || Number.MAX_SAFE_INTEGER;
                return aTime - bTime;
            });
        } else {
            filteredData.sort((a, b) => a.stageTime - b.stageTime);
        }

        // Assign filtered rankings
        filteredData.forEach((athlete, index) => {
            athlete.filteredRank = index + 1;
        });

        return filteredData;
    }

    /**
     * Gets current filter values from DOM
     */
    getCurrentFilters(): FilterConfig {
        const categorySelect = document.getElementById('category-filter') as HTMLSelectElement;
        const distanceSelect = document.getElementById('distance-filter') as HTMLSelectElement;
        const ageSelect = document.getElementById('age-filter') as HTMLSelectElement;

        return {
            category: categorySelect?.value || 'all',
            distance: distanceSelect?.value || 'all',
            ageGroup: ageSelect?.value || 'all'
        };
    }

    /**
     * Calculates distance-only cumulative rank for an athlete
     * (Overall cumulative should only compare athletes within same distance)
     */
    getDistanceOnlyRank(allData: AthleteData[], athlete: AthleteData): number {
        const sameDistanceAthletes = allData.filter(d => d.Distance === athlete.Distance);
        sameDistanceAthletes.sort((a, b) => a.stageTime - b.stageTime);

        const rank = sameDistanceAthletes.findIndex(d => d.Bib === athlete.Bib);
        return rank >= 0 ? rank + 1 : 0;
    }
}
